import { Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { CacheMap } from '@app/classes';
import { Language } from '@app/models';
import { DictionaryService } from '@app/services';
import { DEFAULT_COUNTRY_CODE } from '@app/shared';
import { Utils } from 'app/core/utils';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-language-selector',
  templateUrl: './language-selector.component.html',
  styleUrls: ['./language-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: LanguageSelectorComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => LanguageSelectorComponent),
    },
  ],
})
export class LanguageSelectorComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  @Input() onlyFlag: boolean = false;
  @Input() label: string = 'LABEL.LANGUAGE';
  @Input() languages: CacheMap<Language>;
  @Input() isRequired: boolean = false;
  @Input() selectedLang!: Language | undefined;
  @Input() showLabel: boolean = true;
  @Output() languageChanged = new EventEmitter<Language>();
  language: FormControl = new FormControl('');
  isDisabled: boolean = false;
  subscriptions: Subscription[] = [];

  touched = false;

  constructor(private dictionary: DictionaryService) {}

  private _onChange = (_: Language | undefined) => undefined;
  registerOnChange(fn: (_: Language | undefined) => undefined): void {
    this._onChange = fn;
  }

  private _onTouched = () => undefined;
  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) this.language.disable();
    else this.language.enable();
  }

  validate(control: AbstractControl<any, any>): ValidationErrors | null {
    const isEmpty = !control.value;
    if (!isEmpty && !control.touched) {
      control.markAsTouched();
      control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
    if (!control.touched) return null;
    this.isRequired = control.hasValidator(Validators.required);
    if (this.isRequired && isEmpty) {
      return { required: true };
    }
    return null;
  }

  ngOnDestroy(): void {
    this.subscriptions = Utils.unsubscribe(this.subscriptions);
  }

  ngOnInit(): void {
    const langSub = this.language.valueChanges.subscribe((value: Language) => this._onChange(value));
    this.subscriptions.push(langSub);
  }

  async patchDefaultLanguageValue(): Promise<Language | undefined> {
    this.languages ??= await this.dictionary.getLanguages();
    return this.languages.findByProp('countryCode', DEFAULT_COUNTRY_CODE);
  }

  writeValue(language: Language): void {
    if (language && this.languages) {
      const selectedLanguage = this.languages.with(language.languageId).get(language.languageId);
      this.language.patchValue(selectedLanguage);
      this._onTouched();
    } else {
      this.patchDefaultLanguageValue().then((language) => {
        this.language.patchValue(language);
        this._onChange(language);
      });
    }
  }

  onTouched() {
    this._onTouched();
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  onChangeLanguage({ _, value }: { _: PointerEvent; value: Language }) {
    this._onChange(value);
    this.selectedLang = value;
    this.markAsTouched();
    this.languageChanged.emit(value);
  }
}
